import { useState } from 'react';
import { useHasRole, useMe, useSetRoles, useTeam } from '../api/hooks';
import type { AdminRole, TeamMember } from '../api/types';
import { PageHeader } from '../app/Shell';
import { ROLE_HINT, ROLE_LABEL, USER_STATUS } from '../app/labels';
import { Badge, Button, Card, Checkbox, ConfirmDialog, DataTable, ErrorNote, Field, QueryView, ReasonField, SuccessNote, fmtAgo, reasonError, toast } from '../ui';

const ROLES: AdminRole[] = ['viewer', 'support', 'moderator', 'content', 'owner'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Who can use the console and with which role. Owner only; the last owner can't be removed. */
export function TeamPage() {
  const isOwner = useHasRole('owner');
  const team = useTeam(isOwner);
  const me = useMe();
  const [edit, setEdit] = useState<{ email: string; roles: AdminRole[] } | null>(null);

  if (!isOwner) {
    return (
      <>
        <PageHeader title="團隊與權限" />
        <Card>
          <p className="faint">只有擁有者可以管理團隊。</p>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="團隊與權限"
        description="後台只接受電郵驗證碼登入，並在每次請求時重新讀取角色。對方要先用該電郵登入一次（App、網站或後台皆可）才能授權。"
        actions={
          <Button variant="primary" onClick={() => setEdit({ email: '', roles: ['support'] })}>
            加入成員
          </Button>
        }
      />
      <div className="split">
        <Card flush>
          <QueryView query={team} loading="載入成員…">
            {(rows) => (
              <DataTable
                rows={rows}
                rowKey={(r) => r.userId}
                empty="沒有任何成員。"
                columns={[
                  { key: 'email', header: '電郵', render: (r) => <span>{r.email ?? r.userId}{r.userId === me.data?.userId && <span className="faint">（你）</span>}</span>, sort: (r) => r.email ?? '' },
                  { key: 'roles', header: '角色', render: (r) => <span className="row">{r.roles.map((x) => <Badge key={x} tone={x === 'owner' ? 'accent' : 'neutral'}>{ROLE_LABEL[x]}</Badge>)}</span> },
                  { key: 'status', header: '帳戶', render: (r) => <Badge tone={USER_STATUS[r.status].tone}>{USER_STATUS[r.status].label}</Badge> },
                  { key: 'active', header: '最後活動', render: (r) => fmtAgo(r.lastActiveAt), sort: (r) => r.lastActiveAt },
                  { key: 'edit', header: '', render: (r: TeamMember) => r.email && <Button size="sm" onClick={() => setEdit({ email: r.email!, roles: r.roles })}>修改</Button> },
                ]}
              />
            )}
          </QueryView>
        </Card>
        <aside className="stack">
          <Card title="角色說明">
            <ul style={{ margin: 0, paddingLeft: 18 }} className="small stack">
              {ROLES.map((r) => (
                <li key={r}>
                  <strong>{ROLE_LABEL[r]}</strong>（{r}）：{ROLE_HINT[r]}
                </li>
              ))}
            </ul>
          </Card>
          <Card title="沒有人能登入時">
            <p className="small muted">
              在伺服器執行：
              <br />
              <code>node dist/admin-cli.js grant 電郵 owner</code>
              <br />
              <code>node dist/admin-cli.js list</code>
              <br />
              每次改動都會以 actor「cli」記錄在審計日誌。
            </p>
          </Card>
        </aside>
      </div>
      {edit && <RolesDialog key={edit.email} initial={edit} onClose={() => setEdit(null)} />}
    </>
  );
}

function RolesDialog({ initial, onClose }: { initial: { email: string; roles: AdminRole[] }; onClose: () => void }) {
  const [email, setEmail] = useState(initial.email);
  const [roles, setRoles] = useState<AdminRole[]>(initial.roles);
  const [reason, setReason] = useState('');
  const set = useSetRoles();
  const isNew = !initial.email;
  const ok = EMAIL_RE.test(email.trim()) && !reasonError(reason);

  return (
    <ConfirmDialog
      open
      title={isNew ? '加入成員' : `修改 ${initial.email} 的角色`}
      confirmLabel={roles.length ? '儲存角色' : '移除全部角色'}
      tone={roles.length ? 'primary' : 'danger'}
      busy={set.isPending}
      canConfirm={ok}
      onCancel={onClose}
      onConfirm={() =>
        set.mutate(
          { email: email.trim().toLowerCase(), roles, reason: reason.trim() },
          {
            onSuccess: () => {
              toast(`${email.trim()} 的角色已更新`);
              onClose();
            },
          },
        )
      }
      error={set.isError ? <ErrorNote error={set.error} /> : null}
    >
      {isNew && <Field label="電郵" hint="對方必須已用這個電郵登入過一次。">{(id) => <input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />}</Field>}
      <div className="stack" style={{ gap: 6 }}>
        {ROLES.map((r) => (
          <Checkbox key={r} checked={roles.includes(r)} onChange={(v) => setRoles((cur) => (v ? [...cur, r] : cur.filter((x) => x !== r)))}>
            <strong>{ROLE_LABEL[r]}</strong> <span className="muted small">{ROLE_HINT[r]}</span>
          </Checkbox>
        ))}
      </div>
      {set.isSuccess && <SuccessNote>已更新。</SuccessNote>}
      <ReasonField value={reason} onChange={setReason} />
    </ConfirmDialog>
  );
}
